/**
 * MatchCard Component - Enhanced match display with score breakdown
 * Phase 5 Part 2: Recommendations Dashboard UI
 */

import { useNavigate } from 'react-router-dom';
import {
  Building2,
  TrendingUp,
  TrendingDown,
  Minus,
  ArrowRight,
  Eye,
  Send,
  Clock,
  Star,
  Zap,
  Users,
  MoreHorizontal,
  BarChart3,
  AlertTriangle,
  Activity,
  MessageSquare,
  Lightbulb,
  Target,
  Flame,
  CheckCircle2,
  Sparkles,
  Search,
  Cpu,
  AlertCircle,
  HeartHandshake,
  Compass,
  Globe,
  DollarSign,
  FileText,
  Megaphone,
  User,
  Calendar
} from 'lucide-react';

// Helper function to map backend icon strings to React components
function getReasonIcon(iconName) {
  const iconMap = {
    flame: Flame,
    target: Target,
    sparkles: Sparkles,
    cpu: Cpu,
    search: Search,
    check: CheckCircle2,
    trending: TrendingUp,
    eye: Eye,
    activity: Activity,
    lightbulb: Lightbulb,
    alert: AlertCircle,
    handshake: HeartHandshake,
    compass: Compass,
    globe: Globe,
    dollar: DollarSign,
    zap: Zap,
    file: FileText,
    megaphone: Megaphone,
    user: User,
    calendar: Calendar
  };
  return iconMap[iconName] || Lightbulb;
}

// Helper function to get reason text color based on type
function getReasonColor(type) {
  const colorMap = {
    // Highest priority - urgent signals (red/orange)
    patent_cliff: 'text-red-600',
    pipeline_gap: 'text-red-600',
    competitor_pressure: 'text-red-600',
    // High priority - active buyer signals (orange/amber)
    active_acquirer: 'text-amber-600',
    recent_deal: 'text-amber-600',
    licensing_activity: 'text-amber-600',
    profile_view: 'text-blue-600',
    deal_velocity: 'text-amber-600',
    // Financial capacity signals (green/emerald)
    financial_capacity: 'text-emerald-600',
    deal_size: 'text-emerald-600',
    // Medium priority - strategic alignment (purple/teal)
    strategy_alignment: 'text-purple-600',
    china_interest: 'text-teal-600',
    earnings_mention: 'text-purple-600',
    conference: 'text-teal-600',
    // Standard priority - matching signals
    hot_therapeutic: 'text-orange-600',
    hot_platform: 'text-purple-600',
    therapeutic_overlap: 'text-violet-600',
    platform_match: 'text-indigo-600',
    bd_focus: 'text-pink-600',
    stage_fit: 'text-green-600',
    stage_preference: 'text-green-600',
    deal_structure: 'text-slate-600',
    // Key person changes
    key_person: 'text-blue-600',
    // Low priority - general activity
    clinical_activity: 'text-cyan-600',
    active_buyer: 'text-cyan-600'
  };
  return colorMap[type] || 'text-gray-600';
}

// Helper function to get reason background color based on type
function getReasonBg(type) {
  const bgMap = {
    // Highest priority - urgent signals (red/orange)
    patent_cliff: 'bg-red-50',
    pipeline_gap: 'bg-red-50',
    competitor_pressure: 'bg-red-50',
    // High priority - active buyer signals (orange/amber)
    active_acquirer: 'bg-amber-50',
    recent_deal: 'bg-amber-50',
    licensing_activity: 'bg-amber-50',
    profile_view: 'bg-blue-50',
    deal_velocity: 'bg-amber-50',
    // Financial capacity signals (green/emerald)
    financial_capacity: 'bg-emerald-50',
    deal_size: 'bg-emerald-50',
    // Medium priority - strategic alignment (purple/teal)
    strategy_alignment: 'bg-purple-50',
    china_interest: 'bg-teal-50',
    earnings_mention: 'bg-purple-50',
    conference: 'bg-teal-50',
    // Standard priority - matching signals
    hot_therapeutic: 'bg-orange-50',
    hot_platform: 'bg-purple-50',
    therapeutic_overlap: 'bg-violet-50',
    platform_match: 'bg-indigo-50',
    bd_focus: 'bg-pink-50',
    stage_fit: 'bg-green-50',
    stage_preference: 'bg-green-50',
    deal_structure: 'bg-slate-50',
    // Key person changes
    key_person: 'bg-blue-50',
    // Low priority - general activity
    clinical_activity: 'bg-cyan-50',
    active_buyer: 'bg-cyan-50'
  };
  return bgMap[type] || 'bg-gray-50';
}

// Market hot areas - synced with backend signalScoringService
const MARKET_HOT_AREAS = {
  therapeutic: ['mash', 'masld', 'pain', 'nlrp3', 'il-36', 'urticaria', 'cldn18.2', 'gout', 'csu', 'b-cell malignancy', 'obesity', 'glp-1', 'alzheimer', 'parkinson'],
  platform: ['adc', 'bispecific', 'protein_degrader', 'b_cell_engineering', 'cytokine_engineering', 'nanobody', 'radiopharmaceutical', 'fc_engineering', 'car-t', 'mrna', 'gene therapy']
};

// Generate Why This Match reasons based on match data
function generateMatchReasons(match) {
  const reasons = [];
  const matchingAreas = match.matching_therapeutic_areas || [];
  const matchingPlatforms = match.matching_tech_platforms || [];
  const hotTherapeuticMatches = matchingAreas.filter(area => MARKET_HOT_AREAS.therapeutic.some(hot => area.toLowerCase().includes(hot.toLowerCase())));
  if (hotTherapeuticMatches.length > 0) reasons.push({ icon: Flame, text: 'Hot market: ' + hotTherapeuticMatches.slice(0, 2).join(', '), color: 'text-orange-600', bg: 'bg-orange-50' });
  const hotPlatformMatches = matchingPlatforms.filter(platform => MARKET_HOT_AREAS.platform.some(hot => platform.toLowerCase().includes(hot.toLowerCase())));
  if (hotPlatformMatches.length > 0) reasons.push({ icon: Sparkles, text: 'Trending tech: ' + hotPlatformMatches.slice(0, 2).join(', '), color: 'text-purple-600', bg: 'bg-purple-50' });
  const therapeuticScore = match.therapeutic_match_score || match.therapeutic_area_score || 0;
  if (therapeuticScore >= 60 && matchingAreas.length > 0) reasons.push({ icon: Target, text: 'Strong therapeutic fit (' + matchingAreas.length + ' areas)', color: 'text-violet-600', bg: 'bg-violet-50' });
  if ((match.buyer_need_score || 0) >= 50) reasons.push({ icon: TrendingUp, text: 'Active buyer seeking this', color: 'text-pink-600', bg: 'bg-pink-50' });
  const signalCount = match.signal_count || 0;
  if ((match.activity_score || 0) >= 40 || signalCount >= 3) reasons.push({ icon: Activity, text: 'Recent BD activity (' + signalCount + ' signals)', color: 'text-blue-600', bg: 'bg-blue-50' });
  if ((match.stage_match_score || match.stage_fit_score || 0) >= 70) reasons.push({ icon: CheckCircle2, text: 'Stage aligns with buyer', color: 'text-green-600', bg: 'bg-green-50' });
  return reasons.slice(0, 4);
}

// Score bar component for visualizing individual scores
function ScoreBar({ label, score, weight, color }) {
  return (
    <div className="mb-2">
      <div className="flex justify-between items-center text-xs mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900">{Math.round(score)}</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${score}%`,
            backgroundColor: color
          }}
        />
      </div>
      {weight && (
        <div className="text-[10px] text-gray-400 mt-0.5">{Math.round(weight * 100)}% weight</div>
      )}
    </div>
  );
}

// Quality badge colors and labels
const QUALITY_CONFIG = {
  excellent: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-200',
    label: 'Excellent Match',
    scoreColor: '#10B981'
  },
  strong: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-200',
    label: 'Strong Match',
    scoreColor: '#3B82F6'
  },
  moderate: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    border: 'border-yellow-200',
    label: 'Moderate Match',
    scoreColor: '#F59E0B'
  },
  weak: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-200',
    label: 'Weak Match',
    scoreColor: '#F97316'
  },
  poor: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    border: 'border-gray-200',
    label: 'Low Match',
    scoreColor: '#6B7280'
  }
};

// Action button configurations
const ACTION_CONFIG = {
  reach_out: {
    icon: Send,
    label: 'Reach Out Now',
    className: 'bg-green-600 hover:bg-green-700 text-white'
  },
  monitor: {
    icon: Eye,
    label: 'Monitor Activity',
    className: 'bg-blue-600 hover:bg-blue-700 text-white'
  },
  wait: {
    icon: Clock,
    label: 'Add to Watch List',
    className: 'bg-gray-600 hover:bg-gray-700 text-white'
  },
  low_priority: {
    icon: ArrowRight,
    label: 'View Details',
    className: 'bg-gray-100 hover:bg-gray-200 text-gray-700'
  }
};

export function MatchCard({ match, compact = false, onAction }) {
  const navigate = useNavigate();

  // Get quality configuration
  const quality = match.match_quality || 'moderate';
  const qualityConfig = QUALITY_CONFIG[quality] || QUALITY_CONFIG.moderate;

  // Get action configuration
  const action = match.recommended_action || 'low_priority';
  const actionConfig = ACTION_CONFIG[action] || ACTION_CONFIG.low_priority;
  const ActionIcon = actionConfig.icon;

  // Calculate score for display
  const score = match.final_score || match.match_score || match.score || 0;
  const baseScore = match.base_score || score;

  // Signal boost indicators
  const signalMultiplier = match.signal_multiplier || 1.0;
  const urgencyFactor = match.urgency_factor || 1.0;
  const hasSignalBoost = signalMultiplier > 1.0 || urgencyFactor > 1.0;
  const signalBoostReasons = match.signal_boost_reasons || [];
  const relevantSignals = match.relevant_signals || [];

  // Score change indicator
  const scoreChange = match.score_change || 0;
  const TrendIcon = scoreChange > 0 ? TrendingUp : scoreChange < 0 ? TrendingDown : Minus;
  const trendColor = scoreChange > 0 ? 'text-green-500' : scoreChange < 0 ? 'text-red-500' : 'text-gray-400';

  // Buyer info
  const buyerName = match.buyers?.company_name || match.company_name || 'Unknown Company';
  const buyerHQ = match.buyers?.headquarters || match.headquarters || '';
  const buyerTicker = match.buyers?.stock_ticker || match.stock_ticker || '';
  // Use match reasons from backend if available, otherwise fall back to client-side generation
  const backendReasons = match.match_reasons || [];
  const matchReasons = backendReasons.length > 0
    ? backendReasons.map(r => ({
        ...r,
        icon: getReasonIcon(r.icon),
        color: getReasonColor(r.type),
        bg: getReasonBg(r.type)
      }))
    : generateMatchReasons(match);

  // Handle card click
  const handleClick = () => {
    // Use triple underscore as separator since UUIDs contain dashes
    const matchId = match.id || `${match.seller_id}___${match.buyer_id}`;
    navigate(`/seller/match/${matchId}`);
  };

  // Handle action button click
  const handleAction = (e) => {
    e.stopPropagation();
    // For low_priority (View Details), navigate to match details
    if (action === 'low_priority') {
      handleClick();
      return;
    }
    if (onAction) {
      onAction(match, action);
    }
  };

  // Is this a hot match? (adjusted for new weighted scoring)
  // Hot = high score + either high market opportunity or high signal multiplier
  const isHot = score >= 55 && (
    (match.market_opportunity_score || 0) >= 40 ||
    signalMultiplier >= 1.1
  );

  if (compact) {
    // Compact version for lists
    return (
      <div
        onClick={handleClick}
        className={`flex items-center justify-between p-4 bg-white rounded-lg border ${qualityConfig.border} cursor-pointer hover:shadow-md transition-shadow`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{buyerName}</span>
              {isHot && <Zap className="w-4 h-4 text-orange-500" />}
              {buyerTicker && (
                <span className="text-xs text-gray-500">({buyerTicker})</span>
              )}
            </div>
            <span className="text-sm text-gray-500">{buyerHQ}</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="flex items-center gap-1">
              <span className="text-xl font-bold" style={{ color: qualityConfig.scoreColor }}>
                {Math.round(score)}
              </span>
              {scoreChange !== 0 && (
                <span className={`flex items-center text-xs ${trendColor}`}>
                  <TrendIcon className="w-3 h-3" />
                  {Math.abs(scoreChange)}
                </span>
              )}
            </div>
            <span className={`text-xs px-2 py-0.5 rounded ${qualityConfig.bg} ${qualityConfig.text}`}>
              {qualityConfig.label}
            </span>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </div>
      </div>
    );
  }

  // Full card version
  return (
    <div
      onClick={handleClick}
      className={`bg-white rounded-xl border-l-4 shadow-sm hover:shadow-lg transition-all cursor-pointer overflow-hidden`}
      style={{ borderLeftColor: qualityConfig.scoreColor }}
    >
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between">
          {/* Company Info */}
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
              {match.buyers?.logo_url ? (
                <img
                  src={match.buyers.logo_url}
                  alt={buyerName}
                  className="w-10 h-10 object-contain"
                />
              ) : (
                <Building2 className="w-7 h-7 text-gray-400" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 text-lg">{buyerName}</h3>
                {isHot && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                    <Zap className="w-3 h-3" /> Hot
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">{buyerHQ}</p>
              {buyerTicker && (
                <p className="text-xs text-gray-400 mt-0.5">{buyerTicker}</p>
              )}
            </div>
          </div>

          {/* Score */}
          <div className="text-right">
            <div
              className="text-3xl font-bold"
              style={{ color: qualityConfig.scoreColor }}
            >
              {Math.round(score)}
            </div>
            <div className="flex items-center justify-end gap-1 mt-1">
              <span className={`text-xs px-2 py-0.5 rounded-full ${qualityConfig.bg} ${qualityConfig.text}`}>
                {qualityConfig.label}
              </span>
              {scoreChange !== 0 && (
                <span className={`flex items-center text-xs ${trendColor}`}>
                  <TrendIcon className="w-3 h-3" />
                  {Math.abs(Math.round(scoreChange))}
                </span>
              )}
            </div>
            {/* Signal Boost Indicator */}
            {hasSignalBoost && (
              <div className="flex items-center justify-end gap-2 mt-2">
                {signalMultiplier > 1.0 && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full" title={`Signal boost: ${signalMultiplier.toFixed(2)}x`}>
                    <BarChart3 className="w-3 h-3" />
                    +{Math.round((signalMultiplier - 1) * 100)}%
                  </span>
                )}
                {urgencyFactor > 1.0 && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full" title={`Urgency: ${urgencyFactor.toFixed(2)}x`}>
                    <AlertTriangle className="w-3 h-3" />
                    Urgent
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* WHY THIS MATCH - New Section */}
        {matchReasons.length > 0 && (
          <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-gray-800">Why This Match</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {matchReasons.map((reason, i) => {
                const ReasonIcon = reason.icon;
                return <div key={i} className={'flex items-center gap-2 px-2 py-1.5 rounded-md ' + reason.bg}><ReasonIcon className={'w-4 h-4 ' + reason.color + ' flex-shrink-0'} /><span className={'text-xs font-medium ' + reason.color}>{reason.text}</span></div>;
              })}
            </div>
          </div>
        )}

        {/* Score Breakdown */}
        <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-1">
          <ScoreBar
            label="Therapeutic Areas"
            score={match.therapeutic_match_score || match.therapeutic_area_score || 0}
            weight={0.30}
            color="#8B5CF6"
          />
          <ScoreBar
            label="Buyer Need"
            score={match.buyer_need_score || 0}
            weight={0.20}
            color="#EC4899"
          />
          <ScoreBar
            label="Tech Platforms"
            score={match.platform_match_score || match.tech_platform_score || 0}
            weight={0.12}
            color="#3B82F6"
          />
          <ScoreBar
            label="Stage Fit"
            score={match.stage_match_score || match.stage_fit_score || 0}
            weight={0.12}
            color="#10B981"
          />
          <ScoreBar
            label="Market Opportunity"
            score={match.market_opportunity_score || 0}
            weight={0.12}
            color="#F59E0B"
          />
          <ScoreBar
            label="Buyer Activity"
            score={match.activity_score || 0}
            weight={0.14}
            color="#EF4444"
          />
        </div>

        {/* Signal Boost Reasons */}
        {signalBoostReasons.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-medium text-gray-700">Signal Intelligence</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {signalBoostReasons.slice(0, 3).map((reason, i) => (
                <span key={i} className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-lg">
                  {reason}
                </span>
              ))}
              {signalBoostReasons.length > 3 && (
                <span className="text-xs text-gray-500 self-center">
                  +{signalBoostReasons.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Matching Details */}
        {(match.matching_therapeutic_areas?.length > 0 || match.matching_tech_platforms?.length > 0) && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            {match.matching_therapeutic_areas?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {match.matching_therapeutic_areas.slice(0, 3).map((area, i) => (
                  <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded">
                    {area}
                  </span>
                ))}
                {match.matching_therapeutic_areas.length > 3 && (
                  <span className="text-xs text-gray-500">
                    +{match.matching_therapeutic_areas.length - 3} more
                  </span>
                )}
              </div>
            )}
            {match.matching_tech_platforms?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {match.matching_tech_platforms.slice(0, 3).map((platform, i) => (
                  <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                    {platform}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Footer */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {match.recommended_timing && (
            <span>
              {match.recommended_timing === 'immediate' && 'Act now'}
              {match.recommended_timing === 'this_week' && 'This week'}
              {match.recommended_timing === 'this_month' && 'This month'}
              {match.recommended_timing === 'not_urgent' && 'When available'}
            </span>
          )}
          {match.signal_count > 0 && (
            <span className="ml-3">{match.signal_count} signals</span>
          )}
          {match.profile_view_count > 0 && (
            <span className="ml-3">{match.profile_view_count} views</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Message button - navigates to Messages page with buyer pre-selected */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/seller/messages?buyerId=${match.buyer_id}&buyerName=${encodeURIComponent(buyerName)}`);
            }}
            className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors"
            title="Send Message"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="hidden sm:inline">Message</span>
          </button>
          {/* Request Introduction button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onAction) onAction(match, 'request_intro');
            }}
            className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-purple-600 hover:bg-purple-50 transition-colors"
            title="Request Warm Introduction"
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Intro</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default MatchCard;
